import { useQuery } from '@tanstack/react-query';
import { api } from './api.js';
import type { Sku } from '@z9-store/core';

interface CatalogResponse {
  readonly skus: ReadonlyArray<Sku>;
}

const CATALOG_KEY = ['catalog'] as const;
const skuKey = (skuId: string) => ['catalog', skuId] as const;

export function useCatalog() {
  return useQuery<ReadonlyArray<Sku>>({
    queryKey: CATALOG_KEY,
    queryFn: async () => {
      const { skus } = await api.get<CatalogResponse>('/catalog');
      return skus;
    },
    staleTime: 60_000
  });
}

export function useSku(skuId: string | undefined) {
  return useQuery<Sku | null>({
    queryKey: skuKey(skuId ?? ''),
    enabled: Boolean(skuId),
    queryFn: async () => {
      if (!skuId) return null;
      return api.get<Sku>(`/catalog/${encodeURIComponent(skuId)}`);
    },
    staleTime: 60_000
  });
}

// Group a SKU list by `tier`, preserving the order in which tiers first appear.
// Pure -- exported separately so tests can hit it directly when we add unit
// coverage; for now the e2e test covers the rendered result.
export function groupByTier(skus: ReadonlyArray<Sku>): Array<{ tier: string; skus: Sku[] }> {
  const order: string[] = [];
  const byTier = new Map<string, Sku[]>();
  for (const s of skus) {
    if (!byTier.has(s.tier)) {
      order.push(s.tier);
      byTier.set(s.tier, []);
    }
    byTier.get(s.tier)!.push(s);
  }
  return order.map((tier) => ({ tier, skus: byTier.get(tier)! }));
}
